import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { LibraryPage, ReportConfig } from '@modules/report-generator/interfaces';
import { ChartElement } from '@modules/report-generator/interfaces/elements';
import { BodyCompositionPieChartService } from './body-composition-pie-chart.service';
import { MetabolicFingerprintRadarService } from './metabolic-fingerprint-radar.service';
import { ReportMetabolicCapacitiesGridService } from './report-metabolic-capacities-grid.service';
import { LoadCharacteristicsService } from './load-characteristics.service';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { UniquePageKey, extractTextFromHtml, randomSuffix } from '@modules/report-generator/shared';
import { PPDCrossValidationService } from './ppd-cross-validation.service';
import { createState } from '@shared/helpers/create-state.helper';
import { TYPICAL_ERROR_ENUM } from '@shared/enums/typical-error.enum';
import { DataConfig } from '@shared/interfaces/kinetics-graph.interface';
import { LactateTestDataGraphsService } from './lactate-test-data-graphs.service';
import { MetabolicPlot4, PerformanceDevelopmentChartService } from './performance-development-chart.service';
import { ReportGeneratorStore } from '@modules/report-generator/report-generator.store';
import { TextLanguage } from '@modules/report-generator/types';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatDialogRef } from '@angular/material/dialog';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EditTextDialogComponent } from '../../shared/edit-text-dialog/edit-text-dialog.component';
import { PortaitPreview } from '@modules/report-generator/constants';

@Component({
  selector: 'app-report-chart',
  templateUrl: './report-chart.component.html',
  styleUrls: ['./report-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportChartComponent implements OnInit, OnChanges {
  @Input() element!: ChartElement;
  @Input() reportConfig!: ReportConfig;
  @Input() page?: LibraryPage;

  // For PDF rendering specifically
  @Input() renderForPdf = false;

  private readonly store = inject(ReportGeneratorStore);

  typicalErrorList = createState<Array<any>>([]);
  stepTestConfigGeneral = createState<DataConfig[]>([]);
  kinecticsGraphType = createState<number>(0);
  public TYPICAL_ERROR_ENUM = TYPICAL_ERROR_ENUM;

  private chartsRendered = false;

  testLoaded: boolean = false;
  extractTextFromHtml = extractTextFromHtml;

  // Graph related
  gaugeChartData: Array<Record<string, any>> = [];
  gaugeCanvasSuffix = '';

  constructor(
    private readonly bodyCompositionService: BodyCompositionPieChartService,
    private readonly metabolicFingerprintService: MetabolicFingerprintRadarService,
    private readonly metabolicCapacitiesGridService: ReportMetabolicCapacitiesGridService,
    private readonly loadCharacteristicsService: LoadCharacteristicsService,
    private readonly ppdCrossValidationService: PPDCrossValidationService,
    private readonly lactateTestDataGraphsService: LactateTestDataGraphsService,
    private readonly performanceDevelopmentService: PerformanceDevelopmentChartService,
    private readonly sanitizer: DomSanitizer,
    private readonly dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.testLoaded = this.renderForPdf || Boolean(this.reportConfig?.metabolicProfile);
    this.gaugeCanvasSuffix = this.renderForPdf ? '-pdf' : '';

    if (this.testLoaded && !this.chartsRendered) {
      this.renderCharts();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['reportConfig']) {
      const didTemplateChange = this.didTemplateChange(changes);
      const didThemeColorsChange = this.didThemeColorsChange(changes);
      const didMetabolicProfileLoad = this.didMetabolicProfileLoad(changes);

      if (didMetabolicProfileLoad && !this.chartsRendered) {
        // First load of metabolic profile
        this.testLoaded = true;
        this.renderCharts();
      } else if (didThemeColorsChange && this.isUpdatableChart()) {
        this.chartsRendered = false;

        setTimeout(() => {
          this.testLoaded = Boolean(this.reportConfig?.test) || this.renderForPdf;
          if (this.testLoaded && !this.chartsRendered) {
            this.renderCharts();
          }
        }, 0);
      }
    } else {
      this.testLoaded = Boolean(this.reportConfig?.metabolicProfile) || this.renderForPdf;
      if (this.testLoaded && !this.chartsRendered) {
        this.renderCharts();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyChartForElement();
  }

  get uniquePageKey(): number {
    return UniquePageKey(this.page!);
  }

  renderCharts(): void {
    const metabolicProfile = this.reportConfig?.metabolicProfile;
    if (!metabolicProfile) {
      return;
    }

    if (this.element.type === 'metabolic-capacities-grid') {
      const suffix = '-' + this.uniquePageKey + '-' + this.element.slug;
      this.gaugeCanvasSuffix = this.renderForPdf ? `${suffix}-pdf` : suffix;
      this.gaugeChartData = this.metabolicCapacitiesGridService.generateGaugeChartData(
        metabolicProfile[1],
        this.gaugeCanvasSuffix
      );
    } else if (this.element.type === 'metabolic-capacities-single') {
      const suffix = randomSuffix();
      this.gaugeCanvasSuffix = this.renderForPdf ? `${suffix}-pdf` : suffix;
      const result = this.metabolicCapacitiesGridService.generateGaugeChartDataSingle(
        metabolicProfile[1],
        this.element.subtype!,
        this.gaugeCanvasSuffix
      );
      if (result.ok) {
        this.gaugeChartData = result.value;
      } else {
        const errorMessage = `Unable to render graph '${this.element.subtype!}': ${result.error}`;
        this.store.addRenderError(this.page?.id!, errorMessage);
      }
    }

    setTimeout(() => {
      this.chartsRendered = true;
      switch (this.element.type) {
        case 'body-composition':
          this.renderBodyCompositionPieChart(metabolicProfile);
          break;
        case 'metabolic-fingerprint':
          this.renderMetabolicFingerprint(metabolicProfile);
          break;
        case 'metabolic-capacities-grid':
          this.renderMetabolicCapacitiesGrid(metabolicProfile);
          break;
        case 'metabolic-capacities-single': {
          if (this.gaugeChartData.length > 0) {
            this.renderMetabolicCapacitiesSingle(metabolicProfile, this.element.subtype!, this.gaugeCanvasSuffix);
          }
          break;
        }
        case 'load-charcteristics':
          this.renderLoadCharacteristics(metabolicProfile);
          break;
        case 'load-characteristics-single':
          this.renderLoadCharacteristicsSingle(metabolicProfile, this.element.subtype!);
          break;
        case 'ppd-cross-validation':
          this.renderPPDCrossValidation(metabolicProfile);
          break;
        case 'kinetics':
          this.renderKineticsGraph(metabolicProfile);
          break;
        case 'metabolic-demand':
          this.renderMetabolicDemand(metabolicProfile);
          break;
        case 'lactate-accumulation':
          this.renderLactateAccumulation(metabolicProfile);
          break;
        case 'performance-development':
          this.renderPerformanceDevelopment(metabolicProfile);
          break;
        default:
          break;
      }
    }, 300);
  }

  renderBodyCompositionPieChart(metabolicProfile: Array<any>): void {
    const elementId = ChartConstants.BODY_COMPOSITION.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    this.bodyCompositionService.renderBodyComposition(metabolicProfile[0], elementId, undefined, this.reportConfig);
  }

  renderMetabolicFingerprint(metabolicProfile: Array<any>): void {
    const elementId =
      ChartConstants.METABOLIC_FINGERPRINT.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    this.metabolicFingerprintService.renderMetabolicFingerprint(metabolicProfile[3], elementId, this.reportConfig);
  }

  renderMetabolicCapacitiesGrid(metabolicProfile: Array<any>): void {
    this.metabolicCapacitiesGridService.renderMetabolicCapacitiesGrid(
      metabolicProfile[1],
      this.gaugeCanvasSuffix,
      this.reportConfig
    );
  }

  renderMetabolicCapacitiesSingle(metabolicProfile: Array<any>, subtype: string, suffix: string): void {
    this.metabolicCapacitiesGridService.renderMetabolicCapacitiesSingle(
      metabolicProfile[1],
      subtype,
      this.reportConfig,
      suffix
    );
  }

  renderLoadCharacteristics(metabolicProfile: Array<Record<string, any>>): void {
    let elementId = ChartConstants.LOAD_CHARACTERISTICS.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      elementId = `${elementId}-pdf`;
    }
    this.loadCharacteristicsService.renderLoadCharacteristics(
      metabolicProfile[2],
      this.reportConfig?.test!,
      this.element.variant === 'landscape',
      elementId,
      this.renderForPdf
    );
  }

  renderLoadCharacteristicsSingle(metabolicProfile: Array<Record<string, any>>, subtype: string): void {
    let elementId = ChartConstants.LOAD_CHARACTERISTICS.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      elementId = `${elementId}-pdf`;
    }
    this.loadCharacteristicsService.renderLoadCharacteristicsSingle(
      metabolicProfile[2],
      this.reportConfig?.test!,
      subtype,
      elementId,
      this.renderForPdf
    );
  }

  renderPPDCrossValidation(metabolicProfile: Array<Record<string, any>>): void {
    let elementId: string | undefined =
      ChartConstants.TEST_DATA.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      elementId = `${elementId}-pdf`;
    }
    this.ppdCrossValidationService.renderPPDCrossValidation(
      metabolicProfile[6],
      elementId,
      this.reportConfig?.zoom,
      this.renderForPdf
    );
  }

  renderMetabolicDemand(metabolicProfile: Array<Record<string, any>>): void {
    let elementId = ChartConstants.METABOLIC_DEMAND.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      elementId = `${elementId}-pdf`;
    }
    this.lactateTestDataGraphsService.renderMetabolicDemand(
      metabolicProfile[6],
      this.reportConfig?.test!,
      elementId,
      this.renderForPdf,
      this.reportConfig?.zoom
    );
  }

  renderLactateAccumulation(metabolicProfile: Array<Record<string, any>>): void {
    let elementId = ChartConstants.LACTATE_ACCUMULATION.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      elementId = `${elementId}-pdf`;
    }
    this.lactateTestDataGraphsService.renderLactateAccumulation(
      metabolicProfile[6],
      this.reportConfig?.test!,
      elementId,
      this.renderForPdf,
      this.reportConfig?.zoom
    );
  }

  renderPerformanceDevelopment(metabolicProfile: Array<Record<string, any>>): void {
    let elementId =
      ChartConstants.PERFORMANCE_DEVELOPMENT_3.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      elementId = `${elementId}-pdf`;
    }
    this.performanceDevelopmentService.renderPerformanceDevelopment(
      metabolicProfile[4] as MetabolicPlot4,
      this.reportConfig?.test!,
      elementId,
      this.reportConfig?.zoom
    );
  }

  onEditText(): void {
    const dialogRef: MatDialogRef<EditTextDialogComponent> = this.dialog.open(EditTextDialogComponent, {
      width: `750px`,
      panelClass: 'general-dialog',
      autoFocus: true,
      disableClose: false,
      data: {
        heading: this.getTextHeading(),
        content: this.getTextContent(),
        canEditContent: this.element.content != null,
        canEditHeading: this.element.heading != null,
        editor: {
          width: 700,
          height: 350,
        },
      },
    });

    dialogRef.afterClosed().subscribe(({ heading, content }: { heading: string; content: string }) => {
      this.store.overrideText(UniquePageKey(this.page!), this.element.slug, content, heading);
    });
  }

  getTextContent(): string {
    const language = this.getLanguage();
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    const contentOverride: string = templateOverrides?.[this.uniquePageKey]?.[this.element.slug]?.content;
    return contentOverride ?? this.element.content?.[language] ?? this.element.content?.en ?? '';
  }

  getTextHeading(): string {
    const language = this.getLanguage();
    const templateOverrides = this.reportConfig?.template?.element_config_overrides;
    const contentOverride: string | undefined =
      templateOverrides?.[UniquePageKey(this.page!)]?.[this.element.slug]?.heading;
    return contentOverride ?? this.element.heading?.[language] ?? this.element.heading?.en ?? '';
  }

  getSafeHtml(htmlString: string): SafeHtml {
    // WARNING!! Unsafe
    return this.sanitizer.bypassSecurityTrustHtml(htmlString);
  }

  getTextHeadingColor(): string {
    return this.reportConfig.template?.theme?.colors?.primary || 'rgba(34, 34, 34)';
  }

  getZoomLevel(): number {
    if (this.renderForPdf) {
      return this.reportConfig?.zoom ?? 1;
    }
    return 1;
  }

  renderKineticsGraph(metabolicProfile: Array<Record<string, any>>): void {
    if (!metabolicProfile) {
      return;
    }

    const testDataWrapper = metabolicProfile?.[6];
    const testData =
      testDataWrapper && Array.isArray(testDataWrapper) && testDataWrapper.length > 0
        ? testDataWrapper[0]
        : testDataWrapper;

    if (!testData) {
      return;
    }

    let element_suffix = this.uniquePageKey + '-' + this.element.slug;
    if (this.renderForPdf) {
      element_suffix += '-pdf';
    }
    const kineticsData: Array<any> = testData.kinetics.map((k: any) => ({
      ...k,
      chart_height: this.getElementHeight(),
      element_suffix,
    }));
    this.kinecticsGraphType.set(kineticsData.length);
    if (!kineticsData || kineticsData.length === 0) {
      return;
    }

    if (kineticsData.length === 2) {
      // Issue with the type on the component side
      this.stepTestConfigGeneral.set({
        left: kineticsData[0],
        right: kineticsData[1],
      } as unknown as DataConfig[]);
    } else {
      this.stepTestConfigGeneral.set(kineticsData);
    }
  }

  get isLandscape(): boolean {
    // Portrait by default
    return this.page?.orientation === 'landscape';
  }

  get elementSuffix(): string {
    const elementSuffix = `${this.uniquePageKey}-${this.element.slug}`;
    if (this.renderForPdf) {
      return elementSuffix + '-pdf';
    }
    return elementSuffix;
  }

  private getLanguage(): TextLanguage {
    return this.reportConfig.language || 'en';
  }

  private getElementHeight(): number {
    return (this.element.height / 100) * PortaitPreview.width;
  }

  private didThemeColorsChange(changes: SimpleChanges): boolean {
    const change = changes['reportConfig'];
    const prev = (change.previousValue as ReportConfig)?.template?.theme?.colors;
    const curr = (change.currentValue as ReportConfig)?.template?.theme?.colors;

    if (!prev || !curr) return false;

    return prev.primary !== curr.primary || prev.secondary !== curr.secondary;
  }

  private didMetabolicProfileLoad(changes: SimpleChanges): boolean {
    const change = changes['reportConfig'];
    const prev = (change.previousValue as ReportConfig)?.metabolicProfile;
    const curr = (change.currentValue as ReportConfig)?.metabolicProfile;

    return !prev && Boolean(curr);
  }

  private didTemplateChange(changes: SimpleChanges): boolean {
    const change = changes['reportConfig'];
    return (change.previousValue as ReportConfig)?.template?.id !== (change.currentValue as ReportConfig)?.template?.id;
  }

  private isUpdatableChart(): boolean {
    return [
      'metabolic-fingerprint',
      'body-composition',
      'metabolic-capacities-grid',
      'metabolic-capacities-single',
    ].includes(this.element.type);
  }

  private destroyChartForElement(): void {
    switch (this.element.type) {
      case 'metabolic-fingerprint': {
        const elementId =
          ChartConstants.METABOLIC_FINGERPRINT.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        this.metabolicFingerprintService.destroyChart(elementId);
        break;
      }
      case 'body-composition': {
        const elementId =
          ChartConstants.BODY_COMPOSITION.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        this.bodyCompositionService.destroyChart(elementId);
        break;
      }
      case 'load-charcteristics':
      case 'load-characteristics-single': {
        let elementId =
          ChartConstants.LOAD_CHARACTERISTICS.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        if (this.renderForPdf) {
          elementId = `${elementId}-pdf`;
        }
        this.loadCharacteristicsService.destroyChart(elementId);
        break;
      }
      case 'ppd-cross-validation': {
        let elementId: string | undefined =
          ChartConstants.TEST_DATA.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        if (this.renderForPdf) {
          elementId = `${elementId}-pdf`;
        }
        this.ppdCrossValidationService.destroyChart(elementId);
        break;
      }
      case 'metabolic-demand': {
        let elementId = ChartConstants.METABOLIC_DEMAND.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        if (this.renderForPdf) {
          elementId = `${elementId}-pdf`;
        }
        this.lactateTestDataGraphsService.destroyChart(elementId);
        break;
      }
      case 'lactate-accumulation': {
        let elementId =
          ChartConstants.LACTATE_ACCUMULATION.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        if (this.renderForPdf) {
          elementId = `${elementId}-pdf`;
        }
        this.lactateTestDataGraphsService.destroyChart(elementId);
        break;
      }
      case 'performance-development': {
        let elementId =
          ChartConstants.PERFORMANCE_DEVELOPMENT_3.ELEMENT_ID + '-' + this.uniquePageKey + '-' + this.element.slug;
        if (this.renderForPdf) {
          elementId = `${elementId}-pdf`;
        }
        this.performanceDevelopmentService.destroyChart(elementId);
        break;
      }
      case 'metabolic-capacities-grid':
      case 'metabolic-capacities-single':
        this.metabolicCapacitiesGridService.destroyChartsForSuffix(this.gaugeCanvasSuffix);
        break;
    }
  }
}
